import { z } from "zod";
import { RUBRIC_DIMENSION_KEYS } from "./primitives.js";
/**
 * AN EVIDENCE REFERENCE (coach plan step 5, C05; ADR 0018): one thing the
 * coach pointed at, as the server built it and the client can open.
 *
 * The model never writes one of these. It sees a handle ("E2") beside an
 * excerpt code chose, cites the handle in prose, and code turns the citation
 * into this record: opaque id, where it came from, whose words it is, and a
 * destination the server owns. A reference the client receives is therefore
 * always authorised (it was built from the owner's own completed record) and
 * always attributed (the attribution is a field, not a phrase in prose).
 *
 * Words, not digits, wherever a learner might read them: the moment is an
 * ordinal the client says in words; the reading is one of the four words the
 * record uses. The destination carries the session and the turn because the
 * client needs them to open the debrief; both belong to the learner already.
 */
export const EvidenceSourceKindSchema = z.enum([
    /** A verbatim quotation the Assessor kept beside a reading. */
    "ASSESSMENT_QUOTE",
    /** The Assessor's own reason for a reading below strong. */
    "RECORDED_REASON",
    /** The learner's statement, read from the completed session (ADR 0018, gated). */
    "LEARNER_STATEMENT",
    /** The debrief's rollup or first narrated move. */
    "DEBRIEF_SUMMARY",
]);
export const EvidenceAttributionSchema = z.enum([
    /** The learner's own words: a hypothesis, recommendation, cross-examination answer, bargain decision, or a verified action-turn quote. */
    "LEARNER_WORDS",
    /** An action-turn quote: the Assessor's account of the move as Triage restated it, not verified against the learner's raw text. */
    "ASSESSOR_ACCOUNT",
    /** The Assessor's reason paragraph. */
    "ASSESSOR_REASON",
    /** The debrief. */
    "DEBRIEF",
]);
export const ReadingWordSchema = z.enum(["low", "middling", "strong", "not exercised"]);
export const EVIDENCE_EXCERPT_MAX_CHARS = 400;
export const EvidenceReferenceSchema = z.strictObject({
    /** Opaque and server-issued: a digest of what it points at, so the same moment cited twice is one reference. */
    id: z.string().regex(/^[a-f0-9]{16}$/),
    kind: EvidenceSourceKindSchema,
    attribution: EvidenceAttributionSchema,
    /** The engagement by its client's name, as the learner knows it. */
    engagement: z.string().min(1),
    /** ISO date the engagement finished, or null where the record does not carry one. */
    finishedOn: z.string().nullable(),
    /** The displayed moment ordinal within the engagement (one-based), or null for engagement-level evidence. */
    moment: z.number().int().min(1).nullable(),
    dimension: z.enum(RUBRIC_DIMENSION_KEYS).nullable(),
    reading: ReadingWordSchema.nullable(),
    excerpt: z.string().min(1).max(EVIDENCE_EXCERPT_MAX_CHARS),
    /** A fingerprint of the completed evidence this was built over; a reference from an older version still opens, but the client can say the record has moved on. */
    version: z.string().min(1),
    destination: z.strictObject({
        sessionId: z.string().min(1),
        /** The engine turn the debrief tape lists, or null for engagement-level evidence. Never rendered as a number. */
        turn: z.number().int().min(0).nullable(),
    }),
});
/** At most this many references travel with one reply. */
export const EVIDENCE_REFERENCES_MAX = 6;
export const EvidenceReferencesSchema = z.array(EvidenceReferenceSchema).max(EVIDENCE_REFERENCES_MAX);
//# sourceMappingURL=coach-evidence.js.map